import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OutlineButtonComponent } from '../../../../shared/components/outline-button/outline-button';
import { PopupLayoutComponent } from '../../../../shared/components/popup-layout/popup-layout';
import { PrimaryButtonComponent } from '../../../../shared/components/primary-button/primary-button';
import { ImageEnum } from '../../../../shared/utils/image.enum';
import { isReviewLockedAt } from '../../utils/result-release.util';
import { formatDisplayDateTime } from '../../../../shared/utils/date.util';

type SubmissionStatTone = 'blue' | 'amber' | 'violet' | 'red';

interface SubmissionStatCard {
  id: string;
  label: string;
  value: string;
  helperText: string;
  tone: SubmissionStatTone;
  icon: string;
}

@Component({
  selector: 'app-examination-submitted-popup',
  imports: [CommonModule, PopupLayoutComponent, OutlineButtonComponent, PrimaryButtonComponent],
  templateUrl: './examination-submitted-popup.html',
  styleUrl: './examination-submitted-popup.css',
})
export class ExaminationSubmittedPopupComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @Input() isInProgress = false;
  @Input() totalAttempted = 36;
  @Input() totalQuestions = 200;
  @Input() timeTaken = '3h 27m';
  @Input() remainingTime = '71 min';
  @Input() tertiaryLabel = 'Marked for Review';
  @Input() tertiaryValue = 8;
  @Input() tertiaryHelperText = 'Needs review';
  @Input() unattempted = 164;
  @Input() testName = '';
  @Input() submissionTime?: string;
  /** When review becomes available (ms), per the exam's resultConfig; null when it already is. */
  @Input() reviewUnlockAt: number | null = null;
  @Input() reviewLockedMessage = 'Once the test has expired, you will be able to review your results.';

  @Output() backToDashboard = new EventEmitter<void>();
  @Output() viewResult = new EventEmitter<void>();
  protected readonly imageEnum = ImageEnum;
  protected readonly formatDisplayDateTime = formatDisplayDateTime;
  protected readonly isTooltipVisible = signal(false);
  private readonly nowTick = signal(Date.now());
  private tickTimerId: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.tickTimerId = setInterval(() => this.nowTick.set(Date.now()), 1000);
  }

  ngOnDestroy(): void {
    if (this.tickTimerId) {
      clearInterval(this.tickTimerId);
    }
  }

  protected get isReviewLocked(): boolean {
    return !this.isInProgress && isReviewLockedAt(this.reviewUnlockAt, this.nowTick());
  }

  protected get statCards(): SubmissionStatCard[] {
    return [
      {
        id: 'attempted',
        label: 'Total Attempted',
        value: `${this.totalAttempted}`,
        helperText: `out of ${this.totalQuestions}`,
        tone: 'blue',
        icon: this.imageEnum.CHECK_CIRCLE,
      },
      {
        id: 'time-taken',
        label: 'Time Taken',
        value: this.timeTaken,
        helperText: `Remaining: ${this.remainingTime}`,
        tone: 'amber',
        icon: this.imageEnum.ORANGE_CLOCK,
      },
      {
        id: 'tertiary',
        label: this.tertiaryLabel,
        value: `${this.tertiaryValue}`,
        helperText: this.tertiaryHelperText,
        tone: 'violet',
        icon: this.imageEnum.BADGE_PURPLE_ICON,
      },
      {
        id: 'unattempted',
        label: 'Unattempted',
        value: `${this.unattempted}`,
        helperText: 'Closed now',
        tone: 'red',
        icon: this.imageEnum.CIRCLE_CROSS_RED,
      },
    ];
  }

  protected onBackToDashboard(): void {
    this.backToDashboard.emit();
  }

  protected onViewResult(): void {
    if (this.isReviewLocked) {
      this.isTooltipVisible.set(!this.isTooltipVisible());
      return;
    }

    this.isTooltipVisible.set(false);
    this.viewResult.emit();
  }
}
